/**
 * Voice-capture format decisions, shared by every MediaRecorder call site
 * (mobile QuickCapture, desktop voice notes, desktop dictation).
 *
 * This module is a LEAF — no API/service imports — so presentational
 * components can pull in a constant without dragging the service graph along.
 *
 * Everything here exists because these decisions used to be duplicated per call
 * site and drifted: mobile recorded at the browser default (~128 kbps, 55 MB for
 * a 60-minute note) while desktop hardcoded 128000, and both hardcoded a
 * `.webm` extension that is wrong on Safari.
 */

/** Extensions `audioExtensionForMimeType` can return. */
export type AudioExtension = 'webm' | 'ogg' | 'm4a' | 'mp3' | 'wav';

/** Container families we can validate and name. */
type ContainerKind = 'webm' | 'mp4' | 'ogg' | 'other';

/**
 * Speech bitrate for Opus (Chrome, Android, Firefox).
 *
 * The browser default (~128 kbps) is tuned for music and 4x larger than needed;
 * RFC 6716 puts fullband Opus speech at 28-40 kbps, so 48 kbps has real
 * headroom. The transcription providers can't see the difference anyway (both
 * downsample well below this), so the bitrate we keep is for the stored
 * ARTIFACT — the user's own archival playback — not for the transcript. A
 * 60-minute note lands at ~21 MB here.
 */
export const VOICE_AUDIO_BITS_PER_SECOND = 48000;

/**
 * Speech bitrate for AAC-LC (Safari, which records `audio/mp4`).
 *
 * A little higher than the Opus figure: AAC-LC has no speech-specific coding
 * mode, so it's less efficient than Opus at the same rate. Same archival-quality
 * rationale; applying the Opus number here would trim playback quality on Safari
 * for no upload benefit worth the trade.
 */
export const VOICE_AUDIO_BITS_PER_SECOND_AAC = 64000;

/** Codec-appropriate speech bitrate for a MediaRecorder MIME type. */
export function voiceBitrateForMimeType(mimeType: string): number {
  return containerKind(mimeType) === 'mp4'
    ? VOICE_AUDIO_BITS_PER_SECOND_AAC
    : VOICE_AUDIO_BITS_PER_SECOND;
}

/** Normalise a MIME type to its container family. */
function containerKind(mimeType: string): ContainerKind {
  const base = mimeType.split(';')[0].trim().toLowerCase();
  if (base === 'audio/webm' || base === 'video/webm') return 'webm';
  if (base === 'audio/mp4' || base === 'audio/m4a' || base === 'audio/x-m4a') return 'mp4';
  if (base === 'audio/ogg') return 'ogg';
  return 'other';
}

/**
 * Best audio MIME type this browser/WebView supports for recording.
 *
 * Each format is probed — the fallback is NOT assumed to be supported (Android
 * WebView may not do WebM). Returning '' tells the caller to omit `mimeType`
 * entirely so the browser picks its own default, which is always safe.
 */
export function getOptimalAudioMimeType(): string {
  const formats = [
    'audio/webm;codecs=opus',
    'audio/webm',
    'audio/ogg;codecs=opus',
    'audio/ogg',
    'audio/mp4',
  ];
  // `typeof x?.y` still EVALUATES x, so an optional chain does NOT protect an
  // undeclared binding — it throws ReferenceError. jsdom has no MediaRecorder,
  // so both arms are needed to honour the always-safe '' contract above.
  if (typeof MediaRecorder === 'undefined') return '';
  if (typeof MediaRecorder.isTypeSupported !== 'function') return '';
  for (const fmt of formats) {
    if (MediaRecorder.isTypeSupported(fmt)) return fmt;
  }
  return ''; // Let the browser choose its native default
}

/**
 * File extension matching a MediaRecorder MIME type.
 *
 * Chrome/Android records WebM/Opus while Safari records MP4/AAC, so a hardcoded
 * extension mislabels one of them. Extraction routes on MIME type rather than
 * filename, so this governs what the user sees in their library and gets on
 * download — not whether transcription works.
 *
 * Values must stay a subset of `AUDIO_EXTENSIONS` in @bower/shared and of the
 * backend's telemetry extension allow-list, or the file lands in the "unknown"
 * bucket downstream.
 */
export function audioExtensionForMimeType(mimeType: string): AudioExtension {
  const base = mimeType.split(';')[0].trim().toLowerCase();
  switch (base) {
    case 'audio/ogg':
      return 'ogg';
    case 'audio/mp4':
    case 'audio/m4a':
    case 'audio/x-m4a':
      return 'm4a';
    case 'audio/mpeg':
    case 'audio/mp3':
      return 'mp3';
    case 'audio/wav':
    case 'audio/x-wav':
    case 'audio/wave':
    case 'audio/vnd.wave':
      return 'wav';
    case 'audio/webm':
    case '': // Empty Blob type — nothing better to infer; keep the historic default.
      return 'webm';
    default:
      return 'webm';
  }
}

/** `<baseName>.<ext>` using the extension the recorded container implies. */
export function voiceFileName(baseName: string, mimeType: string): string {
  return `${baseName}.${audioExtensionForMimeType(mimeType)}`;
}

export interface VoiceRecorder {
  readonly recorder: MediaRecorder;
  /** The MIME type actually in effect — never '' — for naming and validation. */
  readonly mimeType: string;
}

/**
 * Build a MediaRecorder configured for speech.
 *
 * Single source of truth for codec selection, bitrate, and the fallback when a
 * WebView rejects the option bag. Passing an option bag can raise
 * NotSupportedError, and an unhandled throw here strands a live mic with a
 * frozen UI — so degrade to the UA default rather than dying.
 *
 * Throws only if the browser cannot construct a MediaRecorder at all; callers
 * must handle that (stop the stream, tell the user).
 */
export function createVoiceRecorder(stream: MediaStream): VoiceRecorder {
  const preferredMimeType = getOptimalAudioMimeType();
  let recorder: MediaRecorder;
  let usedFallback = false;
  try {
    recorder = new MediaRecorder(stream, {
      ...(preferredMimeType && { mimeType: preferredMimeType }),
      audioBitsPerSecond: voiceBitrateForMimeType(preferredMimeType),
    });
  } catch {
    // Option bag rejected — keep recording at the UA default. We lose the size
    // win, which is far better than losing the recording.
    usedFallback = true;
    recorder = new MediaRecorder(stream);
  }
  return {
    recorder,
    // On the fallback path `preferredMimeType` is the type the constructor just
    // DENIED, so reporting it would misname the file and could make
    // isPlausibleAudioContainer reject a valid recording. Trust the recorder's
    // own report there; on the happy path they agree.
    mimeType: (usedFallback ? recorder.mimeType : preferredMimeType) || 'audio/webm',
  };
}

/**
 * Does this blob's leading bytes match the container its MIME type claims?
 *
 * Catches genuinely truncated/corrupt captures without assuming WebM: a
 * WebM-only EBML check rejects every Safari recording as "corrupted", since
 * Safari produces MP4. Containers we can't fingerprint are accepted — a
 * false accept costs a downstream error, a false reject costs the recording.
 */
export async function isPlausibleAudioContainer(blob: Blob, mimeType: string): Promise<boolean> {
  const kind = containerKind(mimeType);
  if (kind === 'other') return true; // Unknown container — nothing to assert.

  const header = new Uint8Array(await blob.slice(0, 12).arrayBuffer());
  if (header.length < 4) return false;

  switch (kind) {
    // EBML magic.
    case 'webm':
      return header[0] === 0x1a && header[1] === 0x45 && header[2] === 0xdf && header[3] === 0xa3;
    // 'ftyp' box type at offset 4.
    case 'mp4':
      return (
        header.length >= 8 &&
        header[4] === 0x66 &&
        header[5] === 0x74 &&
        header[6] === 0x79 &&
        header[7] === 0x70
      );
    // 'OggS' capture pattern.
    case 'ogg':
      return header[0] === 0x4f && header[1] === 0x67 && header[2] === 0x67 && header[3] === 0x53;
    default:
      return true;
  }
}

/**
 * Which container magic (if any) the blob's leading bytes carry.
 *
 * For diagnostics: log THIS, not the raw header bytes. The bytes are container
 * framing (EBML magic / `ftyp` brand / `OggS`), never audio samples — but they
 * still egress to a non-BAA sink via console-log capture, and the classification
 * carries the same debugging signal with nothing to leak.
 */
export async function audioHeaderMagic(blob: Blob): Promise<'ebml' | 'ftyp' | 'oggs' | 'none'> {
  const h = new Uint8Array(await blob.slice(0, 8).arrayBuffer());
  if (h[0] === 0x1a && h[1] === 0x45 && h[2] === 0xdf && h[3] === 0xa3) return 'ebml';
  if (h[4] === 0x66 && h[5] === 0x74 && h[6] === 0x79 && h[7] === 0x70) return 'ftyp';
  if (h[0] === 0x4f && h[1] === 0x67 && h[2] === 0x67 && h[3] === 0x53) return 'oggs';
  return 'none';
}
